import { useState } from 'react';
import { api } from '../../api';
import { isCutChild, pagesLabel } from '../segments/cutChild';
import { docTitle } from '../../domain/documentTitle';

// Why a document is on the "Check these" list (the server's `folder_check`, plan §7e):
// the case's folders say what they hold, and Arc could not file this one by itself.
const WHY = {
  unsure: 'Arc could not tell what kind of document this is',
  no_folder: 'no folder holds this kind of document yet',
  ready: 'a folder holds this kind, but it was not filed when it arrived',
};

const kindLabel = (doc) => String(doc.document_purpose || '').replace(/_/g, ' ');

/**
 * CheckTheseBanner — shown above the list when the "Check these" row of the folder
 * tree is open: what the list is, why each document is on it, and what to do.
 *
 * Props:
 *   docs       — the documents on the list (in the unit on screen)
 *   documents  — every row of the case (to name the file a document was cut from)
 */
export default function CheckTheseBanner({ caseId, docs, documents = [], onFiled }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const ready = docs.filter(d => d.folder_check === 'ready').length;
  // "File these now": the documents a folder already holds the kind of (no model call)
  async function fileNow() {
    setBusy(true);
    setNote('');
    try {
      const out = await api.post(`/documents/case/${caseId}/file-by-rules`);
      setNote(`${out.filed} document${out.filed === 1 ? '' : 's'} filed.`);
      onFiled?.();
    } catch (err) {
      setNote('Could not file them: ' + err.message);
    } finally {
      setBusy(false);
    }
  }
  const names = new Map(documents.map(d => [d.id, d.filename]));
  const title = (d) => (isCutChild(d)
    ? `${docTitle(d, names.get(d.container_id))} (${pagesLabel(d)})`
    : docTitle(d));
  const shown = docs.slice(0, 8);
  return (
    <div className="inv-check-banner" data-testid="inv-check-banner">
      {docs.length === 0 ? (
        <span>Nothing to check — every document Arc could file by itself is in its folder.</span>
      ) : (
        <>
          <strong>{docs.length} document{docs.length === 1 ? '' : 's'} to check.</strong>{' '}
          Arc files a new document into the folder that holds its kind. {docs.length === 1 ? 'This one it' : 'These it'} could
          not file by itself — move {docs.length === 1 ? 'it' : 'each'} to a folder, and what you file by hand stays where you put it.
          <ul>
            {shown.map(d => (
              <li key={d.id} data-testid="inv-check-item" data-reason={d.folder_check}>
                {title(d)}
                <span className="inv-check-why">
                  {' — '}{WHY[d.folder_check] || WHY.unsure}
                  {d.folder_check === 'no_folder' && kindLabel(d) ? ` (${kindLabel(d)})` : ''}
                </span>
              </li>
            ))}
            {docs.length > shown.length && <li>…and {docs.length - shown.length} more in the list below</li>}
          </ul>
          {ready > 0 && (
            <div style={{ marginTop: 8 }}>
              <button className="btn btn-sm btn-primary" data-testid="inv-check-file-now" onClick={fileNow} disabled={busy}>
                {busy ? 'Filing…' : `File ${ready === 1 ? 'the 1 document' : `the ${ready} documents`} Arc has a folder for`}
              </button>
            </div>
          )}
        </>
      )}
      {note && <div data-testid="inv-check-note" style={{ marginTop: 6 }}>{note}</div>}
    </div>
  );
}
